/**
 * Observable connection status for Realtime WebSocket.
 * Components subscribe via listeners; multiple sources update their own state.
 *
 * Multi-source aware: status is "connected" if ANY source is connected.
 * Includes debouncing: brief reconnecting blips (<5s) are suppressed
 * so the UI doesn't "twitch".
 */

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

type Listener = (status: ConnectionStatus) => void;

/** Per-source status tracking – prevents flip-flopping between channels. */
const sourceStatuses = new Map<string, ConnectionStatus>();

/** Expose per-source statuses for diagnostics UI. */
export function getSourceStatuses(): Map<string, ConnectionStatus> {
  return new Map(sourceStatuses);
}

/** The status exposed to subscribers (debounced). Start as 'reconnecting' during startup. */
let publicStatus: ConnectionStatus = 'reconnecting';
const listeners = new Set<Listener>();

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 8000; // suppress reconnecting blips shorter than this
const DISCONNECT_DEBOUNCE_MS = 15000; // extra grace for "disconnected"

function computeAggregateStatus(): ConnectionStatus {
  if (sourceStatuses.size === 0) return 'disconnected';
  // If ANY source is connected, we're connected
  for (const s of sourceStatuses.values()) {
    if (s === 'connected') return 'connected';
  }
  // If any source is reconnecting, we're reconnecting
  for (const s of sourceStatuses.values()) {
    if (s === 'reconnecting') return 'reconnecting';
  }
  return 'disconnected';
}

export function getConnectionStatus(): ConnectionStatus {
  return publicStatus;
}

function emit(status: ConnectionStatus): void {
  if (status === publicStatus) return;
  publicStatus = status;
  listeners.forEach((fn) => fn(status));
}

/**
 * Update the connection status for a named source (e.g. 'user-messages', 'native-push').
 * The aggregate status is computed across all sources.
 */
export function setConnectionStatus(status: ConnectionStatus, source = 'default'): void {
  const prev = sourceStatuses.get(source);
  if (prev === status) return;
  sourceStatuses.set(source, status);

  const aggregate = computeAggregateStatus();

  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  if (aggregate === 'connected') {
    // Going back to connected – emit immediately so users see green fast
    emit('connected');
  } else {
    // Going to reconnecting/disconnected – wait before telling the UI
    // Use a longer debounce for "disconnected" to avoid false negatives
    const delay = aggregate === 'disconnected' ? DISCONNECT_DEBOUNCE_MS : DEBOUNCE_MS;
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      const current = computeAggregateStatus();
      if (current !== 'connected') {
        emit(current);
      }
    }, delay);
  }
}

/**
 * Remove a source entirely (e.g. when unsubscribing a channel).
 */
export function removeConnectionSource(source: string): void {
  sourceStatuses.delete(source);
  // Recompute without debounce if we still have connected sources
  const aggregate = computeAggregateStatus();
  if (aggregate === 'connected') {
    emit('connected');
  }
}

/**
 * Performs a single heartbeat ping to verify connectivity.
 * Called from MainApp's consolidated 30s interval instead of
 * running its own setInterval — reduces total timer count.
 */
export async function heartbeatPingOnce(): Promise<void> {
  try {
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
    if (supabaseUrl) {
      const res = await fetch(`${supabaseUrl}/rest/v1/`, { method: 'HEAD', headers: { 'apikey': (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY || '' } });
      setConnectionStatus(res.ok ? 'connected' : 'reconnecting', 'heartbeat');
    } else {
      const { supabase } = await import('@/integrations/supabase/client');
      const { error } = await supabase.from('system_config').select('key').limit(1);
      setConnectionStatus(error ? 'reconnecting' : 'connected', 'heartbeat');
    }
  } catch {
    setConnectionStatus('reconnecting', 'heartbeat');
  }
}

/**
 * @deprecated Use heartbeatPingOnce() from the MainApp consolidated timer instead.
 * Kept for backward compatibility — starts its own interval.
 */
let _heartbeatInterval: ReturnType<typeof setInterval> | null = null;
export function startHeartbeatPing(): void {
  if (_heartbeatInterval) return;
  heartbeatPingOnce();
  _heartbeatInterval = setInterval(heartbeatPingOnce, 30000);
}

export function stopHeartbeatPing(): void {
  if (_heartbeatInterval) { clearInterval(_heartbeatInterval); _heartbeatInterval = null; }
  removeConnectionSource('heartbeat');
}

export function subscribeConnectionStatus(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
